// =============================================================================
// LIGA AJUPAM — Qué NO es un bug de la app (filtros de ruido de Sentry)
// =============================================================================
// v6.11.149 — Los dos issues que quedaban abiertos en Sentry no describían un
// bug: eran eventos que los filtros YA EXISTENTES querían descartar y no
// reconocían, porque estaban escritos con una redacción distinta de la que
// realmente llega. Misma forma en los dos, y por eso van juntos.
//
// ── LIGA-AJUPAM-56 ──────────────────────────────────────────────────────────
//   TypeError: FetchEvent.respondWith received an error: TypeError: Load failed
//
// Sale del polyfill de `modulepreload` de Vite, cuyo cuerpo es literalmente:
//
//     function processPreload(link) {
//       if (link.ep) return;
//       link.ep = true;
//       fetch(link.href, getFetchOpts(link));   // ← sin .catch()
//     }
//
// Dos hechos que lo explican entero:
//
//   1. El polyfill SÓLO corre en navegadores sin `modulepreload` nativo (arranca
//      con `if (relList.supports('modulepreload')) return`). Safari lo soportó
//      recién en la 17 → el evento es de Mobile Safari 16.6 / iOS 16.7. Encaja.
//   2. Ese fetch es ESPECULATIVO: es un precalentamiento y nadie lo espera. El
//      `import()` de verdad es otro request, con su propia recuperación
//      (`__vitePreload` → 'vite:preloadError' → lazyWithReload). O sea que el
//      fallo del preload no lo ve el usuario, y lo que informa —si informa algo—
//      vuelve a llegar después, esa vez con diagnóstico adjunto.
//
// Como nadie lo captura, cualquier fallo se convierte en unhandled rejection y
// aterriza en Sentry con `level: error`, indistinguible de una pantalla rota.
//
// POR QUÉ LLEGÓ: `ignoreErrors` ya declara que un fallo de red no se reporta…
// pero con la redacción de Chrome ('Failed to fetch') y la de Firefox
// ('NetworkError'). WebKit dice **'Load failed'**. O sea que el MISMO fallo se
// descartaba en Android y escritorio y se reportaba en iOS: el defecto no es el
// fallo de red, es la asimetría.
//
// El envoltorio 'FetchEvent.respondWith received an error:' lo pone nuestro
// Service Worker: el handler de /assets/ propaga un fallo de red rechazando el
// respondWith (deliberado, v6.11.42 — devolver un 503 sintético era peor).
//
// ⚠️ LA PRECISIÓN QUE IMPORTA: ese mismo envoltorio transporta el OTRO caso, que
// sí es señal:
//
//   FetchEvent.respondWith received an error: TypeError: [SW] fallback HTML para un chunk: /assets/X.js
//
// Es la marca de chunk obsoleto que instaló v6.11.74. Filtrar por el envoltorio
// se la comería. Por eso el patrón nombra el error de ADENTRO, no el envoltorio.
//
// ── LIGA-AJUPAM-55 ──────────────────────────────────────────────────────────
//   Error: Error invoking postMessage: Java exception was raised during method invocation
//
// Script de tracking que el navegador in-app de Instagram (Android) inyecta en
// la página; el stack entero es `<anonymous>` + `iabjs://…`, ni un frame nuestro.
// `beforeSend` YA tenía la regla —hay un frame `<anonymous>` Y ningún frame
// `in_app` → descartar— y nunca disparó, porque `in_app` lo decide Sentry del
// lado del servidor y marca los frames `iabjs://` como propios: la segunda mitad
// de la condición daba false. Mismo defecto que arriba, un filtro escrito contra
// una forma y un evento que llega con otra.
//
// La regla nueva no depende de `in_app`: si TODOS los frames vienen de un
// esquema que no es http(s) (`iabjs://`, `chrome-extension://`, `moz-extension://`,
// …) o son `<anonymous>`, no hay código nuestro en el stack. La regla vieja se
// conserva en OR, para que nada de lo que hoy se filtra deje de filtrarse.
// =============================================================================

/** Un frame de stacktrace, en la forma mínima que expone el evento de Sentry. */
export interface NoiseFrame {
  filename?: string;
  abs_path?: string;
  in_app?: boolean;
}

/**
 * Fallo de red del navegador en la redacción de WebKit ('Load failed'), suelto o
 * envuelto por el respondWith del Service Worker.
 *
 * Van ANCLADOS a propósito. `ignoreErrors` hace match por substring, así que un
 * 'Load failed' pelado en la lista descartaría cualquier mensaje que contenga
 * esas dos palabras. Acá el mensaje tiene que ser ESE y nada más.
 *
 * Sólo se lista 'Load failed' porque es lo único que se vio llegar. WebKit tiene
 * otras redacciones ('The network connection was lost.', 'cancelled'), pero un
 * patrón sin un evento real detrás es un límite inventado: si aparecen, se
 * agregan con su evento.
 */
const WEBKIT_NETWORK_FAILURE = [
  /^(?:TypeError:\s*)?Load failed\.?$/,
  /^(?:TypeError:\s*)?FetchEvent\.respondWith received an error:\s*TypeError:\s*Load failed\.?$/,
];

/** Esquema de URL tipo `iabjs://`, `chrome-extension://`, `capacitor://`… */
const NON_WEB_SCHEME = /^[a-z][a-z0-9+.-]*:\/\//i;
const WEB_SCHEME = /^https?:\/\//i;

/**
 * ¿Este frame es de código inyectado desde afuera de la app?
 *
 * Falla hacia el lado seguro: un filename vacío o desconocido cuenta como
 * "podría ser nuestro" (false), así que NO alcanza para descartar el evento.
 * Preferimos un evento de ruido de más que tragarnos uno propio.
 */
function isExternalFrame(file: string): boolean {
  if (file === '<anonymous>') return true;
  if (WEB_SCHEME.test(file)) return false; // http(s): puede ser nuestro bundle
  return NON_WEB_SCHEME.test(file);
}

const fileOf = (frame: NoiseFrame): string => frame.filename || frame.abs_path || '';

/**
 * True si el mensaje es un fallo de red del navegador y no un error de la app.
 *
 * Complementa —no reemplaza— las entradas 'NetworkError' y 'Failed to fetch' de
 * `ignoreErrors`, que cubren Firefox y Chrome. Ésta cubre WebKit.
 */
export function isBrowserNetworkFailure(message: string): boolean {
  const msg = message.trim();
  return WEBKIT_NETWORK_FAILURE.some((re) => re.test(msg));
}

/**
 * True si NINGÚN frame del stack es código nuestro (script externo inyectado).
 *
 * Un evento sin frames nunca se descarta por esta regla: `[].every(...)` da true
 * y eso descartaría todo error sin stacktrace.
 */
export function isExternalScriptError(frames: NoiseFrame[] | undefined): boolean {
  if (!frames || frames.length === 0) return false;
  return frames.every((frame) => isExternalFrame(fileOf(frame)));
}

/**
 * La regla original de v6.4.x: hay un frame `<anonymous>` y ninguno marcado
 * `in_app`. Se conserva para no dejar de filtrar nada de lo que ya se filtraba.
 */
export function isAnonymousThirdPartyError(frames: NoiseFrame[] | undefined): boolean {
  if (!frames || frames.length === 0) return false;
  return frames.some((f) => fileOf(f) === '<anonymous>') && !frames.some((f) => f.in_app);
}

/**
 * Veredicto único que consume `beforeSend`: ¿este evento describe un problema de
 * la app, o del navegador / la red / un script de terceros?
 */
export function isNoiseEvent(message: string, frames: NoiseFrame[] | undefined): boolean {
  return (
    isBrowserNetworkFailure(message) ||
    isExternalScriptError(frames) ||
    isAnonymousThirdPartyError(frames)
  );
}

/** Sólo para el banco de pruebas. */
export const __testing = { isExternalFrame, WEBKIT_NETWORK_FAILURE };
